"use client"

import { useState, type ReactNode } from "react"
import { ChevronDown, Loader2, Table2 } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { useAiDisclosureSummaryQuery, useAiDisclosureHistoryQuery, useAiDisclosureDetailsQuery } from "@/hooks/queries/ai-disclosure-report"
import { DISCLOSURE_REPORT_PAGE_SIZE } from "@/lib/ai-disclosure/report-pagination"

const TABLE_CLASS = "w-full text-left text-sm [&_th]:px-4 [&_th]:py-2 [&_th]:font-medium [&_td]:px-4 [&_td]:py-3 [&_td]:align-top [&_tbody_tr]:border-t"
type RangeProps = { from: string; to: string }

export function DisclosureReportTables({ from, to }: RangeProps) {
  return (
    <div className="space-y-3">
      <ReportSection title="By channel and first recorded outcome" description="Channel outcomes and recorded coverage for the selected period.">
        <SummaryReport from={from} to={to} />
      </ReportSection>
      <ReportSection title="AI disclosure setting history" description="The last recorded state before this range and changes during it. Configuration is not proof of delivery.">
        <HistoryReport from={from} to={to} />
      </ReportSection>
      <ReportSection title="Conversation details" description={`${DISCLOSURE_REPORT_PAGE_SIZE} records per page. Expand evidence to review the complete recorded event history.`}>
        <DetailsReport from={from} to={to} />
      </ReportSection>
    </div>
  )
}

function SummaryReport({ from, to }: RangeProps) {
  const report = useAiDisclosureSummaryQuery(from, to)
  if (!report.data) return <SectionQueryState error={report.error} retry={() => void report.refetch()} />
  const rows = report.data.filter(row => row.noticeKind === "ai")
  const countStatus = (status: string) => rows.filter(row => row.status === status).reduce((count, row) => count + row.conversations, 0)
  const total = rows.reduce((count, row) => count + row.conversations, 0)
  const delivered = countStatus("delivered")
  const disabled = countStatus("disabled")
  const denominator = total - disabled
  const coverage = denominator > 0 ? `${((delivered / denominator) * 100).toFixed(1)}%` : "—"
  const metrics = [["AI conversations", String(total)], ["Delivered", String(delivered)], ["Failed", String(countStatus("failed"))],
    ["Missing records", String(countStatus("missing"))], ["Generated only", String(countStatus("generated"))],
    ["Disabled", String(disabled)], ["Recorded coverage", coverage]]
  return <>        <table className={TABLE_CLASS} aria-label="By channel and first recorded outcome">
          <thead className="bg-muted"><tr>{["Channel", "Notice", "Status", "Conversations"].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{report.data.map(row => (
            <tr key={`${row.channel}-${row.noticeKind}-${row.status}`}>
              <td>{row.channel}</td><td>{row.noticeKind}</td><td>{row.status}</td><td><span key={row.conversations}>{row.conversations}</span></td>
            </tr>
          ))}
          {report.data.length === 0 && <tr><td colSpan={4}>No recorded AI interactions in this period.</td></tr>}
          </tbody>
        </table>
        <table className={`${TABLE_CLASS} border-t`} aria-label="Recorded coverage">
          <thead className="bg-muted"><tr><th scope="col">Metric</th><th scope="col">Value</th></tr></thead>
          <tbody>{metrics.map(([label, value]) => <tr key={label}><th scope="row">{label}</th><td><span key={value}>{value}</span></td></tr>)}</tbody>
        </table>
</>
}

function HistoryReport({ from, to }: RangeProps) {
  const report = useAiDisclosureHistoryQuery(from, to)
  if (!report.data) return <SectionQueryState error={report.error} retry={() => void report.refetch()} />
  return <>        <table className={`${TABLE_CLASS} min-w-[640px]`} aria-label="AI disclosure setting history">
          <thead className="bg-muted"><tr>{["Effective (UTC)", "Change", "AI announcements", "Recorded (UTC)", "Changed by", "Confirmation"].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{report.data.map(change => (
            <tr key={change.id}>
              <td>{change.effectiveAt}</td><td>{change.source === "initial_state" ? "Initial state" : "Setting changed"}</td>
              <td>{change.enabled ? "Enabled" : "Disabled"}</td><td>{change.recordedAt}</td>
              <td className="max-w-48 break-words">{change.actorUserId ?? "No user action recorded"}</td>
              <td className="min-w-48 max-w-80 whitespace-pre-wrap break-words">{change.acceptedConfirmationText ?? "—"}</td>
            </tr>
          ))}
          {report.data.length === 0 && <tr><td colSpan={6}>No setting history recorded for this period.</td></tr>}
          </tbody>
        </table>
</>
}

function DetailsReport({ from, to }: RangeProps) {
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined])
  const report = useAiDisclosureDetailsQuery(from, to, cursors.at(-1))
  if (!report.data) return <SectionQueryState error={report.error} retry={() => void report.refetch()} />
  return <>        <table className={`${TABLE_CLASS} min-w-[680px]`} aria-label="Conversation details">
          <thead className="bg-muted"><tr>{["Channel / notice", "Conversation ID", "First activity (UTC)", "First / latest outcome", "AI setting", "Evidence"].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{report.data.details.map(row => (
            <tr key={row.key}>
              <td><span>{`${row.channel} / ${row.noticeKind}`}</span></td><td className="max-w-52 break-all">{row.conversationRef}</td>
              <td>{row.firstActivityAt}</td><td><span>{`${row.status} / ${row.latestStatus}`}</span></td><td>{settingDescription(row.enabledAtActivity)}</td>
              <td className="min-w-64">
                {row.events.length === 0 ? <span>No recorded evidence</span> : (
                  <details><summary className="cursor-pointer font-medium">View evidence</summary>
                    <table className={`${TABLE_CLASS} mt-2`} aria-label={`Evidence for ${row.conversationRef} ${row.noticeKind}`}>
                      <thead className="bg-muted"><tr>{["Time (UTC)", "Channel", "Status", "Language", "Disclosure text", "Failure reason"].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
                      <tbody>{row.events.map((event, index) => (
                        <tr key={`${event.occurredAt}-${index}`}><td>{event.occurredAt}</td><td>{event.channel}</td><td>{event.status}</td><td>{event.language ?? "—"}</td>
                          <td className="min-w-48 whitespace-pre-wrap break-words">{event.noticeText ?? "—"}</td><td className="min-w-48 break-words">{event.failureReason ?? "—"}</td></tr>
                      ))}</tbody>
                    </table>
                  </details>
                )}
              </td>
            </tr>
          ))}
          {report.data.details.length === 0 && <tr><td colSpan={6}>No conversation details in this period.</td></tr>}
          </tbody>
        </table>

    <div className="flex flex-wrap items-center gap-3 border-t px-4 py-3">
      <Button variant="outline" disabled={cursors.length === 1 || report.isFetching}
        onClick={() => setCursors(previous => previous.slice(0, -1))}>Previous</Button>
      <span key={cursors.length} className="text-sm">{`Page ${cursors.length}`}</span>
      <Button variant="outline" disabled={!report.data.nextCursor || report.isFetching}
        onClick={() => setCursors(previous => [...previous, report.data!.nextCursor!])}>Next</Button>
    </div>
  </>
}

function SectionQueryState({ error, retry }: { error: Error | null; retry: () => void }) {
  if (error) return <div className="space-y-2 p-4"><p role="alert" className="text-sm text-destructive">{error.message}</p><Button variant="outline" onClick={retry}>Retry</Button></div>
  return <div role="status" className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /><span>Loading section…</span></div>
}

function ReportSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="min-w-0 rounded-lg border border-primary/20">
      <CollapsibleTrigger asChild>
        <button type="button" aria-label={title} className="group flex w-full items-center gap-3 rounded-lg bg-muted/50 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Table2 className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{title}</span><span className="mt-0.5 block text-xs text-muted-foreground">{description}</span></span>
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent><div className="overflow-x-auto rounded-b-lg border-t">{expanded ? children : null}</div></CollapsibleContent>
    </Collapsible>
  )
}

function settingDescription(enabled: boolean | null): string {
  if (enabled === null) return "Unknown"
  return enabled ? "Enabled" : "Disabled"
}
